/**
 * Trial Monitoring — Light + Accent Stripe Design
 * Urgency color-coding, summary strip, framer-motion animations
 */
import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/joy'
import { Schedule, Visibility, Search as SearchIcon, Warning, CheckCircle, Close, Timer, MoreTime } from '@mui/icons-material'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { billingApi } from './api'
import { TrialItem } from './types'

dayjs.extend(relativeTime)

const ease = [0.23, 1, 0.32, 1]
const PAGE_SIZE = 15

function getUrgency(trialEnd: string | null): 'expired' | 'critical' | 'healthy' {
    if (!trialEnd) return 'healthy'
    const daysLeft = dayjs(trialEnd).diff(dayjs(), 'day')
    if (daysLeft < 0) return 'expired'
    if (daysLeft <= 2) return 'critical'
    return 'healthy'
}

function getUrgencyStyles(level: 'expired' | 'critical' | 'healthy') {
    switch (level) {
        case 'expired':
            return { row: 'bg-red-50/40 hover:bg-red-50/70', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' }
        case 'critical':
            return { row: 'bg-amber-50/40 hover:bg-amber-50/70', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' }
        case 'healthy':
            return { row: 'hover:bg-gray-50/50', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' }
    }
}

function getTimeLeft(trialEnd: string | null): string {
    if (!trialEnd) return 'N/A'
    return dayjs(trialEnd).fromNow()
}

export default function TrialMonitoring() {
    const navigate = useNavigate()
    const [trials, setTrials] = useState<TrialItem[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchProject, setSearchProject] = useState('')
    const [searchEmail, setSearchEmail] = useState('')
    const [planFilter, setPlanFilter] = useState('all')
    const [expiryFilter, setExpiryFilter] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    // Extend-trial dialog — targets one row; on success only that row is refreshed in place.
    const [extendTarget, setExtendTarget] = useState<TrialItem | null>(null)
    const [extendDate, setExtendDate] = useState('')
    const [extendBusy, setExtendBusy] = useState(false)
    const [extendError, setExtendError] = useState<string | null>(null)

    const minExtendDate = dayjs().add(1, 'day').format('YYYY-MM-DD')
    const maxExtendDate = dayjs().add(1, 'year').format('YYYY-MM-DD')
    const extendDateValid = !!extendDate && !dayjs(extendDate).isBefore(minExtendDate, 'day') && !dayjs(extendDate).isAfter(maxExtendDate, 'day')

    const openExtendDialog = (trial: TrialItem) => {
        setExtendTarget(trial)
        // Default to a week past the current trial end (or a week from now), clamped to the +1y max.
        const base = trial.trialEnd && dayjs(trial.trialEnd).isAfter(dayjs()) ? dayjs(trial.trialEnd) : dayjs()
        const suggested = base.add(7, 'day')
        setExtendDate((suggested.isAfter(maxExtendDate) ? dayjs(maxExtendDate) : suggested).format('YYYY-MM-DD'))
        setExtendError(null)
    }

    const closeExtendDialog = () => {
        if (extendBusy) return
        setExtendTarget(null)
    }

    const handleExtendTrial = async () => {
        if (!extendTarget || !extendDateValid) return
        setExtendBusy(true)
        setExtendError(null)
        try {
            const result = await billingApi.extendTrial(extendTarget.applicationId, { trialEndDate: dayjs(extendDate).endOf('day').toISOString() })
            setTrials((prev) => prev.map((t) => (t.applicationId === extendTarget.applicationId ? { ...t, trialEnd: result.trialEnd } : t)))
            setExtendTarget(null)
        } catch (err: unknown) {
            const axiosErr = err as { response?: { data?: { message?: string } } }
            setExtendError(axiosErr.response?.data?.message || 'Failed to extend trial')
        } finally {
            setExtendBusy(false)
        }
    }

    useEffect(() => {
        billingApi
            .getTrials()
            .then(setTrials)
            .catch((err) => setError(err.message || 'Failed to fetch trials'))
            .finally(() => setLoading(false))
    }, [])

    const filtered = useMemo(() => {
        return trials.filter((t) => {
            if (searchProject && !t.projectName.toLowerCase().includes(searchProject.toLowerCase())) return false
            if (searchEmail && !t.ownerEmail.toLowerCase().includes(searchEmail.toLowerCase())) return false
            if (planFilter !== 'all' && t.plan !== planFilter) return false
            if (expiryFilter === 'expired') return t.trialEnd ? dayjs(t.trialEnd).isBefore(dayjs()) : false
            if (expiryFilter === 'active') return !t.trialEnd || dayjs(t.trialEnd).isAfter(dayjs())
            return true
        })
    }, [trials, searchProject, searchEmail, planFilter, expiryFilter])

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
    const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
    const expiredCount = trials.filter((t) => t.trialEnd && dayjs(t.trialEnd).isBefore(dayjs())).length
    const healthyCount = trials.length - expiredCount
    const hasActiveFilters = searchProject || searchEmail || planFilter !== 'all' || expiryFilter !== 'all'

    const clearFilters = () => {
        setSearchProject('')
        setSearchEmail('')
        setPlanFilter('all')
        setExpiryFilter('all')
        setCurrentPage(1)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <CircularProgress />
            </div>
        )
    }

    return (
        <div className="p-6 w-full">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-5">
                <span>Home</span>
                <span>/</span>
                <span>Billing</span>
                <span>/</span>
                <span className="text-gray-600 font-medium">Trials</span>
            </div>

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="flex items-center gap-3 mb-6"
            >
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
                    <Schedule sx={{ fontSize: 18 }} className="text-amber-600" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Trial Monitoring</h1>
                    <p className="text-sm text-gray-500">Active trials sorted by expiry date (soonest first)</p>
                </div>
            </motion.div>

            {/* Summary Strip */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1, ease }}
                className="grid grid-cols-3 gap-3 mb-6"
            >
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-[3px] border-t-slate-400 p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center">
                        <Timer sx={{ fontSize: 16 }} className="text-slate-600" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Total Trials</p>
                        <p className="text-xl font-bold text-gray-900">{trials.length}</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-[3px] border-t-red-400 p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-red-50 flex items-center justify-center">
                        <Warning sx={{ fontSize: 16 }} className="text-red-600" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Expired</p>
                        <p className="text-xl font-bold text-red-700">{expiredCount}</p>
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm border-t-[3px] border-t-emerald-400 p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-emerald-50 flex items-center justify-center">
                        <CheckCircle sx={{ fontSize: 16 }} className="text-emerald-600" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">Healthy</p>
                        <p className="text-xl font-bold text-emerald-700">{healthyCount}</p>
                    </div>
                </div>
            </motion.div>

            {error && <div className="text-sm px-3 py-2 rounded-lg mb-4 bg-red-50 text-red-700">{error}</div>}

            {/* Trials Table */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease }}>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    {/* Header */}
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <Schedule sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">Active Trials</p>
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">{filtered.length}</span>
                        <p className="text-xs text-gray-400 ml-2">Color-coded by urgency</p>
                    </div>

                    {/* Filters */}
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 flex gap-3 items-end">
                        <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search by Project</label>
                            <div className="relative">
                                <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search project..."
                                    value={searchProject}
                                    onChange={(e) => {
                                        setSearchProject(e.target.value)
                                        setCurrentPage(1)
                                    }}
                                    className="w-full h-9 text-sm pl-8 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                        </div>
                        <div className="flex-1">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Search by Email</label>
                            <div className="relative">
                                <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    placeholder="Search email..."
                                    value={searchEmail}
                                    onChange={(e) => {
                                        setSearchEmail(e.target.value)
                                        setCurrentPage(1)
                                    }}
                                    className="w-full h-9 text-sm pl-8 pr-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                                />
                            </div>
                        </div>
                        <div className="w-32">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Plan</label>
                            <select
                                value={planFilter}
                                onChange={(e) => {
                                    setPlanFilter(e.target.value)
                                    setCurrentPage(1)
                                }}
                                className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            >
                                <option value="all">All Plans</option>
                                <option value="PRO">Pro</option>
                                <option value="ULTRA">Ultra</option>
                            </select>
                        </div>
                        <div className="w-36">
                            <label className="text-xs font-medium text-gray-600 mb-1.5 block">Status</label>
                            <select
                                value={expiryFilter}
                                onChange={(e) => {
                                    setExpiryFilter(e.target.value)
                                    setCurrentPage(1)
                                }}
                                className="w-full h-9 text-sm px-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white"
                            >
                                <option value="all">All Status</option>
                                <option value="expired">Expired</option>
                                <option value="active">Active / Healthy</option>
                            </select>
                        </div>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="h-9 px-3 text-xs text-gray-500 hover:text-red-600 flex items-center gap-1">
                                <Close sx={{ fontSize: 13 }} />
                                Clear
                            </button>
                        )}
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50/80 text-left">
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-600 w-8"></th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Project Name</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Owner</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Plan</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Trial Ends</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Time Left</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Gift</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600">Seats</th>
                                    <th className="px-2 py-3 text-xs font-semibold text-gray-600 text-right pr-5">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                                            {hasActiveFilters ? 'No trials match your filters' : 'No active trials'}
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((trial, idx) => {
                                        const urgency = getUrgency(trial.trialEnd)
                                        const styles = getUrgencyStyles(urgency)
                                        const timeLeft = getTimeLeft(trial.trialEnd)
                                        return (
                                            <motion.tr
                                                key={trial.applicationId}
                                                initial={{ opacity: 0, x: -8 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.2, delay: idx * 0.03, ease }}
                                                className={`${styles.row} cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0 group`}
                                                onClick={() => navigate(`/billing/${trial.applicationId}`)}
                                            >
                                                <td className="py-2.5 pl-4 pr-0">
                                                    <div className={`w-2.5 h-2.5 rounded-full ${styles.dot}`} />
                                                </td>
                                                <td className="py-2.5 px-2">
                                                    <span className="font-medium text-gray-800 group-hover:text-indigo-700 transition-colors">
                                                        {trial.projectName}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-2 text-gray-600 truncate max-w-[180px]">{trial.ownerEmail}</td>
                                                <td className="py-2.5 px-2">
                                                    <span
                                                        className={`text-[10px] font-bold px-2 py-0.5 rounded ${trial.plan === 'PRO' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}
                                                    >
                                                        {trial.plan}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-2 text-gray-600">
                                                    {trial.trialEnd ? dayjs(trial.trialEnd).format('MMM D, YYYY') : '—'}
                                                </td>
                                                <td className="py-2.5 px-2">
                                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${styles.badge}`}>
                                                        {urgency === 'expired' && <Warning sx={{ fontSize: 10 }} className="mr-1 inline" />}
                                                        {timeLeft}
                                                    </span>
                                                </td>
                                                <td className="py-2.5 px-2">
                                                    {trial.isGift ? (
                                                        <span className="bg-purple-50 text-purple-700 text-[10px] px-2 py-0.5 rounded">Gift</span>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="py-2.5 px-2 text-gray-600">{trial.seats || 1}</td>
                                                <td className="py-2.5 px-2 text-right pr-5" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={() => openExtendDialog(trial)}
                                                        title="Extend trial"
                                                        className="w-7 h-7 rounded-md text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                                                    >
                                                        <MoreTime sx={{ fontSize: 16 }} />
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/billing/${trial.applicationId}`)}
                                                        className="w-7 h-7 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 opacity-0 group-hover:opacity-100 transition-all inline-flex items-center justify-center"
                                                    >
                                                        <Visibility sx={{ fontSize: 16 }} />
                                                    </button>
                                                </td>
                                            </motion.tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
                        <p className="text-xs text-gray-500">
                            {filtered.length > 0
                                ? `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filtered.length)} of ${filtered.length}`
                                : '0 results'}
                        </p>
                        {totalPages > 1 && (
                            <div className="flex items-center gap-2">
                                <button
                                    disabled={currentPage <= 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    Previous
                                </button>
                                <span className="text-xs text-gray-600">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    disabled={currentPage >= totalPages}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    className="px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Extend Trial Dialog */}
            <Modal open={!!extendTarget} onClose={closeExtendDialog}>
                <ModalDialog variant="outlined" sx={{ maxWidth: 420, width: '100%' }}>
                    <DialogTitle>Extend Trial</DialogTitle>
                    <DialogContent>
                        <div className="space-y-4 pt-1">
                            <p className="text-sm text-gray-600">
                                {extendTarget?.projectName} <span className="text-gray-400">({extendTarget?.ownerEmail})</span>
                                {extendTarget?.trialEnd && (
                                    <span className="block text-xs text-gray-400 mt-0.5">
                                        Current trial end: {dayjs(extendTarget.trialEnd).format('MMM D, YYYY')}
                                    </span>
                                )}
                            </p>
                            <div>
                                <label className="text-xs font-medium text-gray-600 mb-1.5 block">New trial end date (max 1 year out)</label>
                                <input
                                    type="date"
                                    value={extendDate}
                                    min={minExtendDate}
                                    max={maxExtendDate}
                                    onChange={(e) => setExtendDate(e.target.value)}
                                    disabled={extendBusy}
                                    className="h-9 text-sm px-3 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-44"
                                />
                                {!!extendDate && !extendDateValid && (
                                    <p className="text-xs text-red-600 mt-1.5">Pick a future date no more than 1 year from today</p>
                                )}
                            </div>
                            {extendError && <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">{extendError}</p>}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="plain" color="neutral" onClick={closeExtendDialog} disabled={extendBusy}>
                            Cancel
                        </Button>
                        <button
                            disabled={!extendDateValid || extendBusy}
                            onClick={() => void handleExtendTrial()}
                            className="h-9 px-4 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
                        >
                            {extendBusy ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} /> : 'Extend Trial'}
                        </button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </div>
    )
}
